import { startTransition, useCallback, useEffect, useMemo, useReducer } from 'react';
import { readFilters, writeFilters } from '../filtersPrefs';
import { countPageFilters, DEFAULT_PAGE_FILTERS, type IPageFilters } from '../pageFilters';
import type { ListItemKind } from '../model/listItem';

/**
 * The flags no panel control shows — only a link (an assistant answer's
 * "critical issues") or an older saved set turns them on. While on, the bar
 * shows each as a removable chip, so the list is never narrowed by a filter
 * the reader can't see.
 */
export const LINK_ONLY_FLAGS = ['critical', 'safety', 'overdue'] as const;
export type LinkOnlyFlag = (typeof LINK_ONLY_FLAGS)[number];

type Action =
  | { type: 'types'; value: ListItemKind[] }
  | { type: 'critical' }
  | { type: 'criticalPath' }
  | { type: 'milestone' }
  | { type: 'safety' }
  | { type: 'checksOutstanding' }
  | { type: 'toggleStatus'; value: string }
  | { type: 'hiddenStatuses'; value: string[] }
  | { type: 'search'; value: string }
  | { type: 'professions'; value: string[] }
  | { type: 'clearFlag'; flag: LinkOnlyFlag }
  | { type: 'clear' }
  | { type: 'replace'; value: IPageFilters };

function reducer(state: IPageFilters, a: Action): IPageFilters {
  switch (a.type) {
    case 'types': return { ...state, types: a.value };
    case 'critical': return { ...state, critical: !state.critical };
    case 'criticalPath': return { ...state, criticalPath: !state.criticalPath };
    case 'milestone': return { ...state, milestone: !state.milestone };
    case 'safety': return { ...state, safety: !state.safety };
    case 'checksOutstanding': return { ...state, checksOutstanding: !state.checksOutstanding };
    // A Status checkbox: hide the status, or show it again.
    case 'toggleStatus': {
      const hidden = state.hiddenStatuses;
      return { ...state, hiddenStatuses: hidden.includes(a.value) ? hidden.filter((s) => s !== a.value) : [...hidden, a.value] };
    }
    case 'hiddenStatuses': return { ...state, hiddenStatuses: a.value };
    case 'search': return { ...state, search: a.value };
    case 'professions': return { ...state, professions: a.value };
    case 'clearFlag': return { ...state, [a.flag]: false };
    case 'clear': return DEFAULT_PAGE_FILTERS;
    case 'replace': return a.value;
  }
}

/**
 * `linked` is the filter set a link arrived with (`useLinkedFilters`), or null.
 *
 * It REPLACES the saved set rather than merging into it: the page has to show
 * what the link promised, and a saved "safety only" left on would hide the very
 * issues it opened on.
 */
export function useActivityFilters(linked: IPageFilters | null = null) {
  // Lazy-init from the link when one arrived, else the persisted set, so
  // leaving/returning keeps the filters and a link never flashes the old ones.
  const [filters, dispatch] = useReducer(reducer, linked, (seed) => seed ?? readFilters());

  // A link followed while the page is already open. On mount this dispatches
  // the value the state already holds, which React bails out of.
  useEffect(() => {
    if (linked !== null) dispatch({ type: 'replace', value: linked });
  }, [linked]);

  // Persist on every change (best-effort; guarded in the util).
  useEffect(() => {
    writeFilters(filters);
  }, [filters]);

  // Filter changes re-derive BOTH views' content (the tracker rebuilds and
  // remounts its visible window when the column set changes), so they dispatch
  // at TRANSITION priority — same treatment as the tracker's own level/sort
  // controls. Otherwise a single Clear at 437 columns blocks the click on the
  // whole urgent commit. `search` is the exception: it drives a controlled
  // input, and transitioning a controlled input's own state makes typing lag.
  const dispatchDeferred = useCallback((a: Action) => startTransition(() => dispatch(a)), []);
  const setTypes = useCallback((value: ListItemKind[]) => dispatchDeferred({ type: 'types', value }), [dispatchDeferred]);
  const toggleCritical = useCallback(() => dispatchDeferred({ type: 'critical' }), [dispatchDeferred]);
  const toggleCriticalPath = useCallback(() => dispatchDeferred({ type: 'criticalPath' }), [dispatchDeferred]);
  const toggleMilestone = useCallback(() => dispatchDeferred({ type: 'milestone' }), [dispatchDeferred]);
  const toggleSafety = useCallback(() => dispatchDeferred({ type: 'safety' }), [dispatchDeferred]);
  const toggleChecksOutstanding = useCallback(() => dispatchDeferred({ type: 'checksOutstanding' }), [dispatchDeferred]);
  const toggleStatus = useCallback((value: string) => dispatchDeferred({ type: 'toggleStatus', value }), [dispatchDeferred]);
  const setHiddenStatuses = useCallback((value: string[]) => dispatchDeferred({ type: 'hiddenStatuses', value }), [dispatchDeferred]);
  const setSearch = useCallback((value: string) => dispatch({ type: 'search', value }), []);
  const setProfessions = useCallback((value: string[]) => dispatchDeferred({ type: 'professions', value }), [dispatchDeferred]);
  const clearFlag = useCallback((flag: LinkOnlyFlag) => dispatchDeferred({ type: 'clearFlag', flag }), [dispatchDeferred]);
  const clear = useCallback(() => dispatchDeferred({ type: 'clear' }), [dispatchDeferred]);

  return useMemo(() => ({
    filters,
    activeCount: countPageFilters(filters),
    setTypes,
    toggleCritical,
    toggleCriticalPath,
    toggleMilestone,
    toggleSafety,
    toggleChecksOutstanding,
    toggleStatus,
    setHiddenStatuses,
    setSearch,
    setProfessions,
    clearFlag,
    clear,
  }), [filters, setTypes, toggleCritical, toggleCriticalPath, toggleMilestone, toggleSafety, toggleChecksOutstanding, toggleStatus, setHiddenStatuses, setSearch, setProfessions, clearFlag, clear]);
}
